import { useCallback, useEffect, useState } from 'react';

type VersionPayload = {
  buildId?: string;
};

export function useClientVersionCheck() {
  const embeddedBuildId = process.env.REACT_APP_BUILD_ID || '';
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkForUpdate = useCallback(async () => {
    if (!embeddedBuildId || process.env.NODE_ENV !== 'production') return;

    try {
      const response = await fetch(`/version.json?ts=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) return;

      const payload = (await response.json()) as VersionPayload;
      if (payload.buildId && payload.buildId !== embeddedBuildId) {
        setUpdateAvailable(true);
      }
    } catch {
      // Ignore network errors while checking for a newer bundle.
    }
  }, [embeddedBuildId]);

  useEffect(() => {
    void checkForUpdate();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    window.addEventListener('focus', checkForUpdate);
    document.addEventListener('visibilitychange', onVisible);

    const interval = window.setInterval(
      () => {
        void checkForUpdate();
      },
      5 * 60 * 1000
    );

    return () => {
      window.removeEventListener('focus', checkForUpdate);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable,
    reloadApp: () => window.location.reload(),
    dismissUpdate: () => setUpdateAvailable(false),
  };
}
